<template>
  <settings-menu-layout>
    <row>
      <col12>
        <text-in-modal  v-if="details" :text="details" @close_modal="showDetails(null)"></text-in-modal>

        <v-text-field
          :label="$t('set_jobs.search')"
          prepend-inner-icon="mdi-magnify"
          v-model="filter" />
        
        <v-card class="mb-3 pa-2">
          <v-card-title class="py-2 px-3 text-caption">{{ $t('set_jobs.job_types') }}</v-card-title>
          <v-card-text class="py-2 px-3">
            <v-checkbox
              v-model="autoAnalyseFilter"
              class="mt-0 mb-2"
              density="compact"
              hide-details="auto"
              color="primary"
              :label="$t('set_jobs.filter_auto_analyse')"
              :hint="$t('set_jobs.filter_auto_analyse_hint')"
              persistent-hint
            />
            <v-row dense align="start">
              <v-col cols="12" sm="6" md="3" lg="2" v-for="jobType in availableJobTypes" :key="jobType.value">
                <v-checkbox
                  v-model="selectedJobTypes"
                  :label="jobType.label"
                  :value="jobType.value"
                  :disabled="autoAnalyseFilter"
                  @change="applyTaskTypesToApi"
                  dense
                  hide-details
                  class="ma-0 pa-0 text-caption"
                  label-class="text-caption font-size-small"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-3 pa-2">
          <v-card-text class="py-2 px-3">
            <v-checkbox
              v-model="show_unclaimed_only"
              :label="$t('set_jobs.show_unclaimed_only')"
              dense
              hide-details
              class="ma-0 pa-0"
            ></v-checkbox>
            <v-checkbox
              v-model="show_claimed_only"
              :label="$t('set_jobs.show_claimed_only')"
              dense
              hide-details
              class="ma-0 pa-0"
            ></v-checkbox>
            <v-checkbox
              v-model="show_processed_only"
              :label="$t('set_jobs.show_processed_only')"
              dense
              hide-details
              class="ma-0 pa-0"
            ></v-checkbox>
            <v-checkbox
              v-model="show_unprocessed_only"
              :label="$t('set_jobs.show_unprocessed_only')"
              dense
              hide-details
              class="ma-0 pa-0"
            ></v-checkbox>
          </v-card-text>
        </v-card>

        <v-btn
          class="ma-2"
          color="error"
          variant="outlined"
          size="small"
          :disabled="totalItems === 0 || loading"
          @click="confirm_delete_all_in_filter"
        >
          {{ $t('set_jobs.delete_all_in_filter') }}
        </v-btn>

        <v-btn
          v-if="allPageJobsSelected"
          class="ma-2"
          color="error"
          variant="outlined"
          size="small"
          @click="confirm_delete_selected"
        >
          {{ $t('set_jobs.delete_all') }}
        </v-btn>
        <delete-button
          v-else-if="selectedJobs.length > 0"
          @pressed="confirm_delete_selected"
        ></delete-button>

        <v-data-table-server
          v-model="selectedJobs"
          v-model:page="options.page"
          v-model:items-per-page="options.itemsPerPage"
          v-model:sort-by="options.sortBy"
          show-select
          item-value="id"
          :loading="loading"
          :items="items"
          :items-length="totalItems"
          :headers="headers"
          @update:options="onOptionsUpdate"
        >
          <template v-slot:item.task="{ item }">
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ item.task }}</span>
              </template>
              <span>Job ID: {{ item.id }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.description="{ item }">
            <div role="button" tabindex="0" @click="showDetails(item.description)">
              {{ item.description.substring(0, 30) }} ...
            </div>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ item.created_at }}
          </template>
          <template v-slot:item.progress="{ item }">
            {{ item.progress }}
          </template>
          <template v-slot:item.processed="{ item }">
            {{ item.processed }}
          </template>
          <template v-slot:item.claimed_at="{ item }">
            <div v-if="item.claimed_at">
              {{ formatDate(item.claimed_at) }}
            </div>
            <div v-else style="color: #999;">
              {{ $t('set_jobs.not_claimed') }}
            </div>
          </template>
          <template v-slot:item.on_hold="{ item }">
            <div v-if="item.on_hold" style="color: #ff9800; font-weight: bold;">
              <v-icon size="small" class="mr-1" color="orange">mdi-pause-circle</v-icon>
              {{ $t('set_jobs.on_hold_yes') }}
            </div>
            <div v-else style="color: #4caf50;">
              <v-icon size="small" class="mr-1" color="green">mdi-play-circle</v-icon>
              {{ $t('set_jobs.on_hold_no') }}
            </div>
          </template>
          <template v-slot:item.logs="{ item }">
            <div v-if="item.logs" role="button" tabindex="0" @click="showDetails(item.logs)" style="cursor: pointer; color: #1976d2;">
              <v-icon size="small" class="mr-1">mdi-text-box-outline</v-icon>
              {{ $t('set_jobs.view_logs') }}
            </div>
            <div v-else style="color: #999;">
              {{ $t('set_jobs.no_logs') }}
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon
              size="small"
              class="mr-2"
              @click="delete_job(item)"
            >
              far fa-trash-alt
            </v-icon>
          </template>
          <template v-slot:top>
            <v-dialog v-model="dialog_delete" max-width="500px">
              <v-card>
                <v-card-title v-if="deleteAllInFilter">
                  {{ $t('set_jobs.delete_all_in_filter_confirm', { count: totalItems }) }}
                </v-card-title>
                <v-card-title v-else-if="jobsToDelete.length === 1">
                  {{ $t('set_jobs.delete_confirm_one') }}
                </v-card-title>
                <v-card-title v-else>
                  {{ $t('set_jobs.delete_confirm_many', { count: jobsToDelete.length }) }}
                </v-card-title>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" variant="text" @click="close_delete_dialog">
                    {{ $t('set_jobs.delete_cancel') }}
                  </v-btn>
                  <v-btn color="primary" variant="text" @click="delete_selected_jobs">
                    {{ $t('set_jobs.delete_confirm_ok') }}
                  </v-btn>
                  <v-spacer></v-spacer>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
        </v-data-table-server>
      </col12>
    </row>
  </settings-menu-layout>
</template>

<script>
  import SettingsMenuLayout from "@/components/layout/SettingsMenuLayout";
  import Col12 from "@/components/layout/Col12";
  import Row from "@/components/layout/Row";
  import ApiDatatableMixin from "@/mixins/ApiDatatableMixin";
  import TextInModal from "@/components/TextInModal.vue";
  import DeleteButton from "@/components/form/DeleteButton.vue";
  import { vuetifyHeaders } from "@/utils/vuetifyHeaders";

  const AUTO_ANALYSE_TASK_TYPES = ['llm_analyse', 'sam3_analyse', 'llm_auto_boxes'];

  export default {
    name: "SettingsJobs",
    mixins: [ApiDatatableMixin],
    data () {
      return {
        'api_model': 'job',
        'options': { page: 1, itemsPerPage: 10, sortBy: [{ key: 'created_at', order: 'desc' }] },
        'details': null,
        'selectedJobTypes': ['run_model'],
        'autoAnalyseFilter': false,
        'selectedJobs': [],
        'dialog_delete': false,
        'jobsToDelete': [],
        'deleteAllInFilter': false,
      }
    },
    mounted() {
      this.applyTaskTypesToApi();
    },
    watch: {
      autoAnalyseFilter(enabled) {
        if (enabled) {
          this.selectedJobTypes = [...AUTO_ANALYSE_TASK_TYPES];
        }
        this.applyTaskTypesToApi();
      },
      show_unclaimed_only(enabled) {
        if (enabled) {
          this.show_claimed_only = false;
        }
      },
      show_claimed_only(enabled) {
        if (enabled) {
          this.show_unclaimed_only = false;
        }
      },
      show_processed_only(enabled) {
        if (enabled) {
          this.show_unprocessed_only = false;
        }
      },
      show_unprocessed_only(enabled) {
        if (enabled) {
          this.show_processed_only = false;
        }
      },
    },
    computed: {
      allPageJobsSelected() {
        return this.items.length > 0 && this.selectedJobs.length === this.items.length;
      },
      availableJobTypes() {
        return [
          { value: 'run_model', label: 'Run Model' },
          { value: 'llm_analyse', label: 'Auto analyse (coordinator)' },
          { value: 'sam3_analyse', label: 'Auto analyse (SAM3)' },
          { value: 'llm_auto_boxes', label: this.$t('set_jobs.job_llm_auto_boxes') },
          { value: 'sam3_segment', label: 'SAM3 segment' },
          { value: 'generate_embeddings', label: 'Generate embeddings' },
          { value: 'calculate_annotation_stats', label: 'Calculate Annotation Stats' },
          { value: 'calculate_report', label: 'Calculate Report' },
          { value: 'calculate_conf_matrix_items', label: 'Calculate Confusion Matrix Items' },
          { value: 'calculate_performance_job', label: 'Calculate Performance Job' },
          { value: 'full_calculate_confusion_matrix', label: 'Full Calculate Confusion Matrix' },
          { value: 'train', label: 'Train' },
          { value: 'export', label: 'Export' },
          { value: 'evaluate', label: 'Evaluate' }
        ];
      },
      headers() {
        return vuetifyHeaders([
          { text: this.$t('set_jobs.task'), value: 'task' },
          { text: this.$t('set_jobs.description'), value: 'description' },
          { text: this.$t('set_jobs.created_at'), value: 'created_at' },
          { text: this.$t('set_jobs.progress'), value: 'progress' },
          { text: this.$t('set_jobs.processed'), value: 'processed' },
          { text: this.$t('set_jobs.claimed_at'), value: 'claimed_at' },
          { text: this.$t('set_jobs.on_hold'), value: 'on_hold' },
          { text: this.$t('set_jobs.logs'), value: 'logs', sortable: false },
          { text: this.$t('set_jobs.actions'), value: 'actions', sortable: false },
        ]);
      },
    },
    methods: {
      showDetails(details) {
        this.details = details
      },
      async delete_job(item) {
        this.loading = true;
        await this.$api.job.delete(item);
        this.selectedJobs = this.selectedJobs.filter((id) => id !== item.id);
        await this.loadItems();
        this.loading = false;
      },
      confirm_delete_selected() {
        this.deleteAllInFilter = false;
        this.jobsToDelete = [...this.selectedJobs];
        this.dialog_delete = true;
      },
      confirm_delete_all_in_filter() {
        this.deleteAllInFilter = true;
        this.jobsToDelete = [];
        this.dialog_delete = true;
      },
      close_delete_dialog() {
        this.dialog_delete = false;
        this.jobsToDelete = [];
        this.deleteAllInFilter = false;
      },
      async delete_selected_jobs() {
        this.loading = true;
        this.dialog_delete = false;
        try {
          if (this.deleteAllInFilter) {
            await this.$api.job.delete_in_filter(
              this.filter,
              this.task_types,
              this.show_unclaimed_only,
              this.show_claimed_only,
              this.show_processed_only,
              this.show_unprocessed_only,
            );
            this.selectedJobs = [];
          } else {
            for (const jobId of this.jobsToDelete) {
              await this.$api.job.delete({ id: jobId });
            }
            this.selectedJobs = [];
          }
          this.jobsToDelete = [];
          this.deleteAllInFilter = false;
          await this.loadItems();
        } finally {
          this.loading = false;
        }
      },
      applyTaskTypesToApi() {
        if (this.autoAnalyseFilter) {
          this.task_types = [...AUTO_ANALYSE_TASK_TYPES];
        } else {
          this.task_types = [...this.selectedJobTypes];
        }
      },
      formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString();
      }
    },
    components: {
      DeleteButton,
      TextInModal,
      Row,
      Col12,
      SettingsMenuLayout},
  }
</script>

<style scoped>
.font-size-small {
  font-size: 0.75rem !important;
}
</style>
